import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Route, Scale, Fuel, Moon, Radio } from "lucide-react";

const FEATURES = [
    {
        icon: Route,
        title: "LOW BRIDGE & WEIGHT CLEARANCE GPS",
        span: "md:col-span-2 lg:col-span-7",
        desc: "Never rip a roof off again. Enter exact trailer height, gross weight, and HAZMAT class for 100% compliant turn-by-turn navigation.",
        badge: "14'6\" GUARANTEE",
        testid: "feature-card-clearance",
        extra: (
            <div className="mt-6 border-2 border-[#0E0D0C]">
                <div className="hazard h-4" />
                <div className="font-mono2 flex items-center justify-between bg-[#0E0D0C] px-4 py-2 text-[10px] tracking-[0.25em] text-[#E5A93C]">
                    <span>CLEARANCE 14'6" — VERIFIED</span>
                    <span className="text-[#2E7D32]">PASS</span>
                </div>
            </div>
        ),
    },
    {
        icon: Scale,
        title: "LIVE SCALE & WEIGH STATION BYPASS",
        span: "md:col-span-1 lg:col-span-5",
        desc: "Real-time crowdsourced and DOT updates on scale house openings, pre-pass alerts, and inspection checks.",
        badge: "SCALES: OPEN / CLOSED",
        testid: "feature-card-weigh-station",
    },
    {
        icon: Fuel,
        title: "DIESEL BARGAIN FINDER",
        span: "md:col-span-1 lg:col-span-4",
        desc: "Compare gallon rates across Loves, Pilot Flying J, TA Petro, and independents. Filter by showers and overnight parking.",
        badge: "SAVE $0.45/GAL",
        testid: "feature-card-diesel",
    },
    {
        icon: Moon,
        title: "PARKING OCCUPANCY RADAR",
        span: "md:col-span-1 lg:col-span-4",
        desc: "Live crowd radar shows open rest-area stalls before your HOS clock runs out. No more shoulder nights.",
        badge: "HOS SAFE",
        testid: "feature-card-parking",
    },
    {
        icon: Radio,
        title: "CB RADIO DIGITAL MESH",
        span: "md:col-span-2 lg:col-span-4",
        desc: "Geo-pinned voice warnings from drivers 10 miles ahead — smoky bears, lane closures, weather delays.",
        badge: "CH 19 LIVE",
        testid: "feature-card-cb-radio",
    },
];

export default function Features() {
    const navigate = useNavigate();
    return (
        <section id="features" data-testid="features-section" className="relative px-6 py-24 lg:px-16 lg:py-32">
            <div className="mx-auto max-w-7xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-mono2 text-xs font-bold tracking-[0.35em] text-[#E5A93C]">
                        BULLETIN No. 66 — APP FEATURES
                    </p>
                    <h2 className="font-display mt-4 text-4xl uppercase tracking-wide text-[#F6F2E8] sm:text-5xl md:text-6xl">
                        BUILT FOR THE <span className="text-[#D32F2F]">LONG HAUL</span>
                    </h2>
                    <div className="hazard mt-6 h-3 w-40 border-2 border-[#0E0D0C]" />
                </motion.div>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-12">
                    {FEATURES.map((f, i) => (
                        <motion.article
                            key={f.testid}
                            data-testid={f.testid}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                            className={`enamel-card relative flex flex-col border-2 border-[#E5A93C]/30 bg-[#24211E] p-8 shadow-[5px_5px_0px_0px_#0E0D0C] ${f.span}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <f.icon className="h-9 w-9 text-[#E5A93C]" strokeWidth={1.75} />
                                <span className="font-mono2 border border-[#161413] bg-[#E5A93C] px-2 py-1 text-[10px] font-bold tracking-widest text-[#161413]">
                                    {f.badge}
                                </span>
                            </div>
                            <h3 className="font-display mt-6 text-2xl uppercase tracking-wider text-[#F6F2E8] sm:text-3xl">
                                {f.title}
                            </h3>
                            <p className="font-mono2 mt-3 text-sm leading-relaxed text-[#C2C7CE]">{f.desc}</p>
                            {f.extra}
                        </motion.article>
                    ))}
                </div>

                <div className="mt-10 text-center">
                    <button
                        data-testid="features-manifest-btn"
                        onClick={() => navigate("/features")}
                        className="btn-press font-display border-2 border-[#0E0D0C] bg-[#D32F2F] px-6 py-3 text-lg tracking-widest text-[#F6F2E8] shadow-[4px_4px_0px_#0E0D0C]"
                    >
                        READ THE FULL FEATURE MANIFEST →
                    </button>
                </div>
            </div>
        </section>
    );
}
